<template>
  <b-modal
      v-bind="$props"
      :id="id"
      centered
      footer-class="border-0 pt-0"
      hide-header
      no-close-on-backdrop
      no-close-on-esc
  >
    <div class="text-center py-2">
      <div class="d-flex justify-content-center mb-3">
        <img :src="AlertIcon" alt="Alert Icon" width="40" height="40" />
      </div>
      <h4>{{ cancelConfirmTitle }}</h4>
      <p class="m-0 text-muted" v-html="cancelConfirmDescription"></p>
    </div>
    <validation-observer ref="form" tag="div" class="text-start mt-3">
      <validation-provider
          v-slot="{ validated, errors }"
          :name="lodash.startCase($t('sales_plans.modal_messages.cancel_reason_label'))"
          rules="required"
          vid="description"
      >
        <b-form-group
            :invalid-feedback="errors[0]"
            :label="lodash.startCase($t('sales_plans.modal_messages.cancel_reason_label'))"
            label-class="sales-plan-modal-label"
            :state="errors[0] ? false : (validated ? true : null)"
            label-for="cancel-description"
            class="asterisk-after-text"
        >
          <b-form-textarea
              id="cancel-description"
              v-model="form.description"
              :placeholder="$t('sales_plans.modal_messages.cancel_reason_placeholder')"
              :state="errors[0] ? false : (validated ? true : null)"
              aria-required="true"
              rows="4"
              required
          ></b-form-textarea>
        </b-form-group>
      </validation-provider>
    </validation-observer>
    <template #modal-footer>
      <div class="d-flex gap-3 w-100">
        <b-button
            :disabled="loading"
            class="w-50"
            variant="light"
            @click="handleCancel"
        >
          {{ $t('Cancel') }}
        </b-button>
        <b-button
            :disabled="loading"
            class="w-50 m-0"
            variant="danger"
            @click="handleConfirm"
        >
          <b-spinner v-if="loading" class="me-2" small></b-spinner>
          {{ lodash.startCase($t('sales_plans.buttons.yes_cancel')) }}
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import ConfirmationModal from '@/components/modals/confirmation-modal.vue'
import AlertIcon from '@/assets/icons/AlertIcon.svg'
import Swal from 'sweetalert2'

export default {
  name: 'sales-plan-cancel-confirmation',
  extends: ConfirmationModal,
  props: {
    id: {default: 'sales-plan-cancel-confirmation-modal'},
    hideHeader: {default: true},
    title: {default: ''},
    salesPlan: {type: Object, default: () => ({})},
  },
  data() {
    return {
      AlertIcon,
      loading: false,
      targetModel: null,
      form: {
        description: '',
      },
    };
  },
  computed: {
    hasApprovedSteps() {
      return (this.targetModel?.approval_progress?.completed_count ?? 0) > 0
    },
    targetModelKey() {
      return this.targetModel?.id
          ?? this.targetModel?.sales_plan_id
          ?? this.$route.params.key
          ?? null
    },
    targetModelName() {
      return this.targetModel?.name || '-'
    },
    cancelConfirmTitle() {
      return `${this.$t('sales_plans.modal_messages.confirm_cancel_title')}?`
    },
    cancelConfirmDescription() {
      const key = this.hasApprovedSteps
          ? 'sales_plans.modal_messages.{something}_confirm_cancel_approved_description'
          : 'sales_plans.modal_messages.{something}_confirm_cancel_description'
      const description = this.$t(key)
      const safeSomething = this.lodash.escape(this.targetModelName)

      return description.replace('{something}', `<strong>${safeSomething}</strong>`)
    },
  },
  methods: {
    show(model) {
      this.targetModel = model ?? null
      this.form.description = ''
      this.$bvModal.show(this.id)
      this.$nextTick(() => {
        this.$refs.form?.reset()
      })
    },
    handleCancel() {
      if (this.loading) return
      this.hide()
    },
    async handleConfirm() {
      if (!this.$refs.form) return

      const isValid = await this.$refs.form.validate()
      if (!isValid) return

      const salesPlanKey = this.targetModelKey
      if (!salesPlanKey) {
        return
      }

      const description = this.form.description.trim()
      this.loading = true

      return this.$axios.post(`/v1/sales-plans/${salesPlanKey}/cancel`, {description})
          .then(() => {
            this.hide()

            return Swal.fire({
              title: this.$t('Success') + '!',
              text: this.$t('sales_plans.modal_messages.cancel_success_message', {something: salesPlanKey}),
              type: 'success',
              confirmButtonClass: 'btn btn-success btn-fill',
              buttonsStyling: false
            }).then(() => {
              this.$emit('cancelled', salesPlanKey)
            })
          })
          .catch((error) => {
            return Swal.fire({
              title: this.$t('Failed') + '!',
              text: (error.response && (error.response.data || {}).message) || error.message,
              type: 'danger',
              confirmButtonClass: 'btn btn-danger btn-fill',
              buttonsStyling: false
            })
          })
          .finally(() => {
            this.loading = false
          })
    },
  }
}
</script>

<style scoped lang="scss">
@import "../../design/custom/components/sales-plan-label";

::v-deep {
  .sales-plan-modal-label {
    @include sales-plan-label;
  }

  .asterisk-after-text {
    label::after {
      content: " *";
      color: #DC2626;
    }
  }
  .modal-body {
    color: var(--neutral-gray-800, #454646);
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
  }

  .modal-footer {
    border: none;
  }
}
</style>
